import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Share, Plus, Apple, Smartphone, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Big/bold "Download App" CTA for the landing hero.
 *
 * Detection tree:
 * 1. Already installed as PWA (display-mode: standalone) -> hide entirely.
 * 2. `beforeinstallprompt` fired (Chrome/Edge Android + Desktop) -> single-tap install.
 * 3. iOS Safari (no beforeinstallprompt) -> open a modal with "Tap Share → Add to Home Screen".
 * 4. Everything else (desktop Safari, Firefox) -> modal with per-browser steps.
 */
export function DownloadApp({ size = "lg", variant = "hero", className = "" }) {
  const [installEvent, setInstallEvent] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [platform, setPlatform] = useState("unknown");

  useEffect(() => {
    // Detect platform
    const ua = navigator.userAgent || navigator.vendor || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /android/i.test(ua);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    if (isIOS) setPlatform("ios");
    else if (isAndroid) setPlatform("android");
    else if (isSafari) setPlatform("safari-desktop");
    else setPlatform("desktop");

    // Detect already-installed
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) setIsInstalled(true);

    // Listen for Chrome's install prompt event
    const handler = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => {
      setIsInstalled(true);
      setInstallEvent(null);
      toast.success("Aura installed to your home screen");
    });
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handleClick = async () => {
    // Case 1: Chrome/Android has fired install prompt — use it directly
    if (installEvent) {
      installEvent.prompt();
      const { outcome } = await installEvent.userChoice;
      if (outcome === "accepted") {
        setIsInstalled(true);
        setInstallEvent(null);
      }
      return;
    }
    // Case 2: Show manual instructions modal (iOS + fallback)
    setModalOpen(true);
  };

  if (isInstalled) return null;

  // Size + variant classes
  const baseSizeCls =
    size === "xl"
      ? "h-16 md:h-20 px-10 md:px-14 text-lg md:text-xl"
      : size === "lg"
      ? "h-14 px-8 text-base md:text-lg"
      : "h-11 px-6 text-sm";
  const variantCls =
    variant === "hero"
      ? "bg-[#D5FF3F] text-[#0B0C0E] hover:bg-[#E8FF6C] shadow-[0_10px_40px_rgba(213,255,63,0.35)] hover:shadow-[0_10px_50px_rgba(213,255,63,0.55)] transition-shadow"
      : "btn-lime";

  return (
    <>
      <Button
        onClick={handleClick}
        data-testid="download-app-cta"
        className={`rounded-full font-bold uppercase tracking-wide ${baseSizeCls} ${variantCls} ${className}`}
      >
        <Download size={size === "xl" ? 22 : 18} className="mr-3" strokeWidth={2.5} />
        Download App Now
      </Button>

      {/* Manual install modal (iOS Safari / fallback) */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent
          className="bg-[#131518] border border-[#26282C] text-[#F5F3EE] max-w-md"
          data-testid="download-app-modal"
        >
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-sm bg-[#D5FF3F] flex items-center justify-center">
                <Download size={20} className="text-[#0B0C0E]" strokeWidth={2.5} />
              </div>
              <DialogTitle className="font-display text-2xl">
                Install Aura on your phone
              </DialogTitle>
            </div>
            <DialogDescription className="text-[#A8ADA5] pt-1">
              No app store needed. Works exactly like a native app — full-screen, push
              notifications, offline mode.
            </DialogDescription>
          </DialogHeader>

          {platform === "ios" ? (
            <div className="space-y-4 mt-2">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#D5FF3F]">
                <Apple size={14} /> iPhone / iPad — Safari
              </div>
              <ol className="space-y-4">
                <InstallStep
                  n="1"
                  icon={<Share size={16} className="text-[#D5FF3F]" />}
                  title="Tap the Share button"
                  body="At the bottom of Safari (looks like a square with an arrow ↑)."
                />
                <InstallStep
                  n="2"
                  icon={<Plus size={16} className="text-[#D5FF3F]" />}
                  title="Scroll and tap 'Add to Home Screen'"
                  body="You may need to scroll down in the share sheet."
                />
                <InstallStep
                  n="3"
                  icon={<Check size={16} className="text-[#7CFF9B]" />}
                  title="Tap 'Add' in the top-right"
                  body="Aura's icon will appear on your home screen. Tap to launch."
                />
              </ol>
              <div className="mt-4 pt-4 border-t border-[#26282C] text-xs text-[#6D716C]">
                Only works in <span className="text-[#F5F3EE]">Safari</span>. If you&rsquo;re in
                Chrome or another browser on iPhone, open this page in Safari first.
              </div>
            </div>
          ) : platform === "android" ? (
            <div className="space-y-4 mt-2">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#D5FF3F]">
                <Smartphone size={14} /> Android — Chrome
              </div>
              <ol className="space-y-4">
                <InstallStep
                  n="1"
                  icon={<span className="text-[#D5FF3F] font-bold text-lg">⋮</span>}
                  title="Tap the 3-dot menu"
                  body="Top-right corner of Chrome."
                />
                <InstallStep
                  n="2"
                  icon={<Plus size={16} className="text-[#D5FF3F]" />}
                  title="Tap 'Install app' or 'Add to Home screen'"
                  body="The label depends on your Chrome version — either works."
                />
                <InstallStep
                  n="3"
                  icon={<Check size={16} className="text-[#7CFF9B]" />}
                  title="Confirm 'Install'"
                  body="Aura will be added to your home screen and app drawer."
                />
              </ol>
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#D5FF3F]">
                <Smartphone size={14} /> Desktop / other browser
              </div>
              <ol className="space-y-4">
                <InstallStep
                  n="1"
                  icon={<Download size={16} className="text-[#D5FF3F]" />}
                  title="Look for the install icon"
                  body="In Chrome/Edge, an install icon (⊕) appears at the right edge of the address bar."
                />
                <InstallStep
                  n="2"
                  icon={<Plus size={16} className="text-[#D5FF3F]" />}
                  title="Click 'Install Aura Credit'"
                  body="The site becomes a standalone app on your desktop."
                />
                <InstallStep
                  n="3"
                  icon={<Check size={16} className="text-[#7CFF9B]" />}
                  title="Or on your phone"
                  body="Open auracleancredit.com on Safari (iPhone) or Chrome (Android) and tap Share/Menu → Add to Home Screen."
                />
              </ol>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function InstallStep({ n, icon, title, body }) {
  return (
    <li className="flex items-start gap-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#0B0C0E] border border-[#D5FF3F]/25 flex items-center justify-center">
        <span className="ticker text-sm text-[#D5FF3F]">{n}</span>
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 text-[#F5F3EE] font-medium">
          {icon} {title}
        </div>
        <div className="text-sm text-[#A8ADA5] mt-0.5 leading-relaxed">{body}</div>
      </div>
    </li>
  );
}
